import { z } from "zod";
import { industryMatchModes } from "./industry-taxonomy.js";
import { isValidIanaTimeZone } from "./datetime.js";
import { isHunterIndustry, normalizeHunterIndustry } from "./hunter-industries.js";
export * from "./datetime.js";
export * from "./hunter-industries.js";
export * from "./industry-taxonomy.js";
export const userRoles = ["ADMIN", "OPERATOR", "VIEWER", "CLOSER_ADMIN", "CLOSER_OPERATOR"];
export const closerOpportunityStatuses = ["NEW", "ASSIGNED", "CONTACTED", "MEETING_PENDING", "NEGOTIATION", "WON", "LOST", "DISQUALIFIED"];
export const managedUsernameSchema = z.string().trim().toLowerCase().min(3).max(50).regex(/^[a-z0-9._-]+$/, "Usá sólo letras minúsculas, números, punto, guion o guion bajo");
export const managedPasswordSchema = z.string().min(12, "La contraseña debe tener al menos 12 caracteres").max(128, "La contraseña no puede superar 128 caracteres");
export const createUserSchema = z.object({
    username: managedUsernameSchema,
    displayName: z.string().trim().min(2).max(120),
    role: z.enum(userRoles),
    password: managedPasswordSchema,
});
export const updateUserSchema = z.object({
    displayName: z.string().trim().min(2).max(120).optional(),
    role: z.enum(userRoles).optional(),
    active: z.boolean().optional(),
}).refine((value) => Object.keys(value).length > 0, "Debe indicarse al menos un campo");
export const resetUserPasswordSchema = z.object({ password: managedPasswordSchema });
export const campaignStatuses = ["DRAFT", "READY", "RUNNING", "PAUSED", "MONITORING", "ACTION_REQUIRED", "BLOCKED", "DEGRADED", "COMPLETED", "CANCELLED", "ARCHIVED"];
export const runStatuses = ["PENDING_AUTHORIZATION", "PENDING", "RUNNING", "WAITING_DIRECTION", "WAITING_STAGE_REVIEW", "WAITING_APPROVAL", "WAITING_INBOUND", "MONITORING_INBOUND", "ACTION_REQUIRED", "BLOCKED", "DEGRADED", "COMPLETED", "FAILED", "CANCELLED"];
export const stepStatuses = ["PENDING", "RUNNING", "WAITING_APPROVAL", "BLOCKED", "COMPLETED", "SKIPPED", "FAILED", "CANCELLED", "SEND_UNCERTAIN"];
export const approvalStatuses = ["PENDING", "APPROVED", "REJECTED", "EXPIRED", "INVALIDATED"];
export const providerStatuses = ["LIVE", "DISCONNECTED", "ERROR", "DEGRADED", "SIMULATED"];
export const integrationProviders = ["HERMES", "HUNTER", "HUBSPOT", "GOOGLE_WORKSPACE", "TWILIO_WHATSAPP", "N8N"];
export const integrationModes = ["DISABLED", "SIMULATED", "LIVE"];
export const channelTypes = ["EMAIL", "WHATSAPP"];
export const publicSignalPolicies = ["OPTIONAL", "REQUIRED", "DISABLED"];
export const targetRoleSources = ["DIRECTOR_AI", "MANUAL"];
export const campaignAudienceTypes = ["B2B", "B2C"];
export const campaignSourceModes = ["SEARCH", "IMPORTED", "HYBRID"];
export const campaignEmailVerificationModes = ["NONE", "HUNTER"];
export const publicSignalTypes = ["PROJECT", "TRANSACTION", "ALLIANCE", "IMPLEMENTATION", "EXPANSION", "OPENING", "HIRING", "TECHNICAL_EVENT", "DOCUMENTED_INITIATIVE"];
export const hunterHeadcounts = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5001-10000", "10001+"];
export const hunterHeadcountOptions = [
    { value: "1-10", minEmployees: 1, maxEmployees: 10 },
    { value: "11-50", minEmployees: 11, maxEmployees: 50 },
    { value: "51-200", minEmployees: 51, maxEmployees: 200 },
    { value: "201-500", minEmployees: 201, maxEmployees: 500 },
    { value: "501-1000", minEmployees: 501, maxEmployees: 1000 },
    { value: "1001-5000", minEmployees: 1001, maxEmployees: 5000 },
    { value: "5001-10000", minEmployees: 5001, maxEmployees: 10000 },
    { value: "10001+", minEmployees: 10001, maxEmployees: 2_147_483_647 },
];
export function employeeMatchesHeadcount(employeeCount, selectedRanges) {
    if (!selectedRanges?.length)
        return null;
    return selectedRanges.some((value) => {
        const range = hunterHeadcountOptions.find((option) => option.value === value);
        return Boolean(range && employeeCount >= range.minEmployees && employeeCount <= range.maxEmployees);
    });
}
export const runStepTypes = [
    "HERMES_STRATEGY",
    "COMPANY_DISCOVERY",
    "CONTACT_ENRICHMENT",
    "QUALIFICATION",
    "CRM_SYNC",
    "OUTREACH_DRAFT",
    "CLAIM_CHECK",
    "HUMAN_APPROVAL",
    "SEND",
    "INBOUND_PROCESSING",
    "MEETING",
    "FINALIZE"
];
export const inboundClassifications = [
    "MEETING_INTENT_NO_SLOT",
    "MEETING_SLOT_PROPOSED",
    "MEETING_SLOT_CONFIRMED",
    "INTERESTED",
    "NOT_INTERESTED",
    "QUESTION",
    "DO_NOT_CONTACT",
    "OUT_OF_OFFICE",
    "REFERRAL",
    "UNKNOWN_REQUIRES_REVIEW"
];
export const journeyStatuses = [
    "QUEUED", "DRAFT_PENDING", "PENDING_APPROVAL", "APPROVED_QUEUED",
    "SENT", "WAITING_REPLY", "FOLLOW_UP_DUE", "FOLLOW_UP_PENDING_APPROVAL",
    "REPLIED", "QUESTION_PENDING_RESPONSE", "MEETING_INTENT", "PENDING_SCHEDULING",
    "SLOT_OPTIONS_PENDING_APPROVAL", "WAITING_SLOT_CONFIRMATION", "MEETING_PENDING_APPROVAL",
    "MEETING_SCHEDULED", "REFERRED", "HUMAN_HANDOFF", "NOT_INTERESTED",
    "DO_NOT_CONTACT", "BOUNCED", "OUT_OF_OFFICE", "SNOOZED", "NO_RESPONSE",
    "MANUAL_REVIEW", "FAILED_REQUIRES_REVIEW", "CANCELLED"
];
export const inboundClassificationResultV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    category: z.enum(inboundClassifications),
    evidence: z.string(),
    confidence: z.number().min(0).max(1),
    reason: z.string(),
    meeting_modality: z.enum(["VIRTUAL", "IN_PERSON", "UNSPECIFIED"]).optional(),
    meeting_modality_question: z.boolean().optional(),
    proposed_date: z.string().optional(),
    proposed_time: z.string().optional(),
    timezone: z.string().optional(),
    meeting_search_window: z.discriminatedUnion("mode", [
        z.object({ mode: z.literal("DEFAULT") }),
        z.object({
            mode: z.literal("RANGE"),
            starts_on: z.string().regex(/^20\d{2}-\d{2}-\d{2}$/),
            ends_before: z.string().regex(/^20\d{2}-\d{2}-\d{2}$/),
            timezone: z.string().min(1),
            source_text: z.string().min(1),
            interpretation: z.string().min(1),
        }),
    ]).optional(),
    mentioned_people: z.array(z.string()).default([]),
    mentioned_emails: z.array(z.string().email()).default([]),
    requires_response: z.boolean(),
    requires_human_review: z.boolean(),
    semantic_intent: z.enum(["REQUESTING_INFORMATION", "GENERAL_QUESTION", "EXPRESSED_INTEREST", "REFERRAL", "OTHER"]).optional(),
    rule_id: z.string().optional(),
    classifier_version: z.string().optional(),
    latest_inbound_text: z.string().optional(),
    quoted_thread_context: z.string().optional(),
    quoted_headers: z.array(z.string()).optional(),
    signature: z.string().optional(),
    safe_thread_summary: z.string().optional(),
    questions: z.array(z.object({
        id: z.string(),
        topic: z.enum(["SCOPE", "IMPLEMENTATION", "REQUIREMENTS", "PRICING", "TIMELINE", "OTHER"]),
        text: z.string(),
    })).optional(),
    warnings: z.array(z.string()).optional(),
});
export const inboundAgentInterpretationV1Schema = inboundClassificationResultV1Schema.extend({
    agent: z.literal("sdr-outreach"),
    prompt_version: z.string().min(1),
    generation_mode: z.literal("AI"),
});
export const conversationMessageDraftV1Schema = z.object({
    schema_version: z.literal("1.0"),
    subject: z.string().min(1),
    body: z.string().min(1),
    agent: z.literal("sdr-outreach"),
    prompt_version: z.string().min(1),
    generation_mode: z.literal("AI"),
    warnings: z.array(z.string()).default([]),
});
const campaignIndustrySchema = z.string().trim().min(1).transform((value) => normalizeHunterIndustry(value) ?? value);
const campaignIndividualCriteriaSchema = z.object({
    locations: z.array(z.string().trim().min(1)).default([]),
    occupations: z.array(z.string().trim().min(1)).default([]),
    interests: z.array(z.string().trim().min(1)).default([]),
}).default({ locations: [], occupations: [], interests: [] });
const campaignAudienceSchema = z.object({
    type: z.enum(campaignAudienceTypes).default("B2B"),
    individualCriteria: campaignIndividualCriteriaSchema,
}).default({ type: "B2B", individualCriteria: { locations: [], occupations: [], interests: [] } });
const campaignAcquisitionSchema = z.object({
    source: z.enum(campaignSourceModes).default("SEARCH"),
    emailVerification: z.enum(campaignEmailVerificationModes).default("HUNTER"),
}).default({ source: "SEARCH", emailVerification: "HUNTER" });
const individualProspectingPolicySchema = z.object({
    publicSourceDiscovery: z.enum(["ALLOWED", "NOT_APPLICABLE"]).default("ALLOWED"),
    requireExplicitPublishedEmail: z.boolean().default(true),
    allowAuthenticatedSources: z.literal(false).default(false),
    allowSensitiveInferences: z.literal(false).default(false),
}).default({
    publicSourceDiscovery: "ALLOWED",
    requireExplicitPublishedEmail: true,
    allowAuthenticatedSources: false,
    allowSensitiveInferences: false,
});
export const campaignConfigSchema = z.object({
    audience: campaignAudienceSchema,
    messageSource: z.enum(["AGENT", "TEMPLATES"]).default("AGENT"),
    copyPolicy: z.enum(["AS_CONFIGURED", "TEMPLATE_OR_AGENT"]).default("AS_CONFIGURED"),
    acquisition: campaignAcquisitionSchema,
    icp: z.object({
        industries: z.array(campaignIndustrySchema).default([]).transform((values) => [...new Set(values)]),
        countries: z.array(z.string().min(2)).default([]),
        employeeMin: z.number().int().nonnegative().default(0),
        employeeMax: z.number().int().positive().default(1),
        headcountRanges: z.array(z.enum(hunterHeadcounts)).min(1).optional(),
        targetRoles: z.array(z.string().min(1)).default([]),
        targetRoleSource: z.enum(targetRoleSources).default("MANUAL"),
        industryMatchMode: z.enum(industryMatchModes).default("PREFERENCE")
    }),
    goal: z.object({
        targetCompanies: z.number().int().min(1),
        targetContacts: z.number().int().min(1),
        targetMeetings: z.number().int().min(0).max(100)
    }),
    limits: z.object({
        maxEmailsPerDay: z.number().int().min(1).max(100),
        maxWhatsAppPerDay: z.number().int().min(0).max(50),
        maxCompaniesPerRun: z.number().int().min(1).default(25),
        maxContactsPerRun: z.number().int().min(1).default(100),
        requireApproval: z.boolean().default(true)
    }),
    objective: z.string().trim().max(1000).default(""),
    tone: z.string().trim().max(200).default("consultivo, profesional y breve"),
    restrictions: z.array(z.string().trim().min(1)).default([]),
    excludedCustomers: z.array(z.string().trim().min(1)).default([]),
    approvedClaims: z.array(z.string().trim().min(1)).default([]),
    forbiddenClaims: z.array(z.string().trim().min(1)).default([]),
    regions: z.array(z.string().trim().min(1)).default([]),
    enabledChannels: z.array(z.enum(channelTypes)).min(1).default(["EMAIL"]),
    personalization: z.object({
        publicSignalPolicy: z.enum(publicSignalPolicies).default("DISABLED"),
        allowedPublicSignalTypes: z.array(z.enum(publicSignalTypes)).default([...publicSignalTypes]),
        decisionOwner: z.literal("DIRECTOR_AI").default("DIRECTOR_AI"),
    }).default({ publicSignalPolicy: "DISABLED", allowedPublicSignalTypes: [...publicSignalTypes], decisionOwner: "DIRECTOR_AI" }),
    schedule: z.object({
        startsAt: z.string().datetime().optional(),
        outreachEndsAt: z.string().datetime().optional(),
        timezone: z.string().min(1).refine(isValidIanaTimeZone, "Seleccioná una zona horaria IANA válida").default("America/Argentina/Buenos_Aires"),
        enabledWeekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).refine((days) => new Set(days).size === days.length, "Los días habilitados no pueden repetirse").default([1, 2, 3, 4, 5]),
        sendWindowStart: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Hora de inicio inválida").default("00:00"),
        sendWindowEnd: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Hora de finalización inválida").default("23:59"),
        monitoringDays: z.number().int().min(1).max(90).default(7),
    }).default({
        timezone: "America/Argentina/Buenos_Aires",
        enabledWeekdays: [1, 2, 3, 4, 5],
        sendWindowStart: "00:00",
        sendWindowEnd: "23:59",
        monitoringDays: 7,
    }),
    followUpPolicy: z.object({
        firstBusinessDay: z.number().int().min(1).max(30).default(3),
        secondBusinessDay: z.number().int().min(1).max(60).default(7),
        closeBusinessDay: z.number().int().min(1).max(90).default(12),
    }).default({ firstBusinessDay: 3, secondBusinessDay: 7, closeBusinessDay: 12 }),
    meeting: z.object({
        calendarId: z.string().trim().min(1),
        organizerEmail: z.string().email(),
        timezone: z.string().trim().min(1).refine(isValidIanaTimeZone, "Seleccioná una zona horaria IANA válida"),
        durationMinutes: z.number().int().min(15).max(240),
        workdayStart: z.string().regex(/^\d{2}:\d{2}$/),
        workdayEnd: z.string().regex(/^\d{2}:\d{2}$/),
        minimumNoticeHours: z.number().int().min(1).max(720),
        optionCount: z.number().int().min(2).max(3),
        searchHorizonDays: z.number().int().min(1).max(60),
        addGoogleMeet: z.boolean().default(true),
    }).optional(),
    initialAssetId: z.string().nullable().optional(),
    hermesFailurePolicy: z.enum(["BLOCK", "DEGRADED"]).default("BLOCK")
}).superRefine((value, context) => {
    const usesB2bSearch = value.audience.type === "B2B" && value.acquisition.source !== "IMPORTED";
    if (usesB2bSearch) {
        if (!value.icp.industries.length)
            context.addIssue({ code: "custom", path: ["icp", "industries"], message: "Definí al menos una industria para la búsqueda B2B" });
        if (!value.icp.countries.length)
            context.addIssue({ code: "custom", path: ["icp", "countries"], message: "Definí al menos un país para la búsqueda B2B" });
        if (!value.icp.targetRoles.length)
            context.addIssue({ code: "custom", path: ["icp", "targetRoles"], message: "Definí al menos un rol para la búsqueda B2B" });
        if (value.icp.employeeMax < value.icp.employeeMin)
            context.addIssue({ code: "custom", path: ["icp", "employeeMax"], message: "employeeMax debe ser mayor o igual a employeeMin" });
        if (value.icp.headcountRanges?.length) {
            const selected = hunterHeadcountOptions.filter((range) => value.icp.headcountRanges.includes(range.value));
            if (value.icp.employeeMin !== selected[0]?.minEmployees || value.icp.employeeMax !== selected.at(-1)?.maxEmployees)
                context.addIssue({ code: "custom", path: ["icp", "headcountRanges"], message: "Los límites deben coincidir con los rangos de Hunter seleccionados" });
        }
    }
    if (value.audience.type === "B2C" && value.acquisition.emailVerification === "HUNTER")
        context.addIssue({ code: "custom", path: ["acquisition", "emailVerification"], message: "Hunter no está habilitado para campañas B2C" });
    if (value.audience.type === "B2B" && value.acquisition.source === "SEARCH" && value.acquisition.emailVerification !== "HUNTER")
        context.addIssue({ code: "custom", path: ["acquisition", "emailVerification"], message: "La búsqueda B2B existente utiliza Hunter" });
    if (value.audience.type === "B2C" && value.acquisition.source !== "IMPORTED") {
        const criteria = value.audience.individualCriteria;
        if (!(criteria.locations.length || criteria.occupations.length || criteria.interests.length))
            context.addIssue({ code: "custom", path: ["audience", "individualCriteria"], message: "Definí al menos un criterio personal para buscar prospectos B2C" });
    }
    if (value.goal.targetMeetings > value.goal.targetContacts)
        context.addIssue({
            code: "custom",
            path: ["goal", "targetMeetings"],
            message: "targetMeetings no puede superar targetContacts",
        });
    if (value.schedule.startsAt && value.schedule.outreachEndsAt && value.schedule.outreachEndsAt <= value.schedule.startsAt)
        context.addIssue({ code: "custom", path: ["schedule", "outreachEndsAt"], message: "outreachEndsAt debe ser posterior a startsAt" });
    if (value.schedule.sendWindowEnd <= value.schedule.sendWindowStart)
        context.addIssue({ code: "custom", path: ["schedule", "sendWindowEnd"], message: "La ventana de envío debe terminar después de comenzar" });
    if (value.meeting && value.meeting.workdayEnd <= value.meeting.workdayStart)
        context.addIssue({ code: "custom", path: ["meeting", "workdayEnd"], message: "La jornada de reuniones debe terminar después de comenzar" });
    if (!(value.followUpPolicy.firstBusinessDay < value.followUpPolicy.secondBusinessDay
        && value.followUpPolicy.secondBusinessDay < value.followUpPolicy.closeBusinessDay))
        context.addIssue({ code: "custom", path: ["followUpPolicy"], message: "La política de seguimiento debe respetar el orden día 3 < día 7 < cierre" });
});
/**
 * Operational capacity follows the requested outcome instead of a fixed UI
 * ceiling. One company can yield at most one selected contact in a run, so a
 * proportional reserve is required to absorb companies without a valid
 * target-role contact.
 */
export function campaignCompanyCapacity(targetCompanies, targetContacts) {
    const required = Math.max(targetCompanies, targetContacts);
    return required + Math.max(2, Math.ceil(required / 2));
}
const createCampaignBaseSchema = z.object({
    name: z.string().trim().min(3).max(120),
    description: z.string().trim().max(1000).default(""),
    offerId: z.string().nullable().optional(),
    config: campaignConfigSchema,
    meetingTeamMemberIds: z.array(z.string().min(1)).max(20).refine((values) => new Set(values).size === values.length, "Los integrantes no pueden repetirse").optional(),
    closerAdminId: z.string().min(1).nullable().optional(),
    closerWorkflowEnabled: z.boolean().optional(),
});
const validateWritableCampaignIndustries = (value, context) => {
    if (!value.config || value.config.audience.type !== "B2B" || value.config.acquisition.source === "IMPORTED")
        return;
    for (const [index, industry] of (value.config?.icp.industries ?? []).entries()) {
        if (!isHunterIndustry(industry))
            context.addIssue({
                code: "custom",
                path: ["config", "icp", "industries", index],
                message: `La industria «${industry}» no pertenece al catálogo oficial de Hunter`,
            });
    }
};
export const createCampaignSchema = createCampaignBaseSchema.extend({
    sourceCampaignId: z.string().min(1).optional(),
}).superRefine(validateWritableCampaignIndustries);
export const createMeetingTeamMemberSchema = z.object({
    firstName: z.string().trim().min(2).max(80),
    lastName: z.string().trim().min(2).max(80),
    email: z.string().trim().toLowerCase().email().max(254),
    jobTitle: z.string().trim().min(2).max(160),
});
export const updateMeetingTeamMemberSchema = createMeetingTeamMemberSchema.partial().extend({
    active: z.boolean().optional(),
}).refine((value) => Object.keys(value).length > 0, { message: "Indicá al menos un campo para actualizar" });
export const campaignMeetingTeamUpdateSchema = z.object({
    memberIds: z.array(z.string().min(1)).max(20).refine((values) => new Set(values).size === values.length, "Los integrantes no pueden repetirse"),
});
const offerClaimsSchema = z.array(z.string().trim().min(3).max(500)).min(1, "Definí al menos un claim autorizado").max(50);
export const createOfferSchema = z.object({
    name: z.string().trim().min(3).max(160),
    description: z.string().trim().min(10).max(4000),
    claims: offerClaimsSchema,
    active: z.boolean().default(true),
});
export const updateOfferSchema = createOfferSchema.partial().refine((value) => Object.keys(value).length > 0, { message: "Indicá al menos un campo para actualizar" });
export function campaignReadinessIssues(input) {
    const parsed = campaignConfigSchema.safeParse(input.config);
    if (!parsed.success)
        return parsed.error.issues.map((issue) => issue.message);
    const issues = [];
    if (!input.offerId)
        issues.push("Seleccioná una oferta");
    if (!parsed.data.objective.trim())
        issues.push("Definí un objetivo comercial");
    if (parsed.data.acquisition.source === "IMPORTED") {
        if (!input.hasImportedContacts)
            issues.push("Cargá el CSV de contactos antes de activar esta campaña");
        else if (parsed.data.audience.type === "B2B")
            issues.push("Habilitá la importación B2B con verificación opcional de Hunter antes de activar esta campaña");
    }
    if (parsed.data.acquisition.source === "HYBRID") {
        if (!input.hasImportedContacts)
            issues.push("Cargá el CSV de contactos antes de activar esta campaña");
        else if (parsed.data.audience.type === "B2B")
            issues.push("Habilitá la combinación B2B de la base importada con Hunter antes de activar esta campaña");
    }
    return issues;
}
const assetFieldsSchema = z.object({
    name: z.string().trim().min(2).max(160),
    kind: z.string().trim().min(2).max(80),
    url: z.string().trim().url().nullable().optional(),
    content: z.string().trim().max(20000).nullable().optional(),
    approved: z.boolean(),
});
const assetHasEvidence = (value) => Boolean(value.url?.trim() || value.content?.trim());
export const createAssetSchema = assetFieldsSchema.extend({
    approved: z.boolean().default(false),
}).refine(assetHasEvidence, {
    message: "La evidencia debe incluir una URL o contenido",
});
export const updateAssetSchema = assetFieldsSchema.partial().refine((value) => Object.keys(value).length > 0, { message: "Indicá al menos un campo para actualizar" });
const nonEmptyList = z.array(z.string().trim().min(1)).default([]);
export const campaignBriefV1Schema = z.object({
    schemaVersion: z.literal("1.0"),
    campaignId: z.string().min(1),
    campaignRunId: z.string().min(1),
    campaignName: z.string().min(1),
    description: z.string(),
    audience: campaignAudienceSchema,
    acquisition: campaignAcquisitionSchema,
    messageSource: z.enum(["AGENT", "TEMPLATES"]).default("AGENT"),
    copyPolicy: z.enum(["AS_CONFIGURED", "TEMPLATE_OR_AGENT"]).default("AS_CONFIGURED"),
    individualProspectingPolicy: individualProspectingPolicySchema,
    offer: z.object({
        id: z.string().nullable(),
        name: z.string(),
        description: z.string(),
        facts: nonEmptyList,
        snapshotHash: z.string().optional(),
        frozenAt: z.string().optional(),
        sourceVersion: z.string().optional(),
        evidence: z.array(z.object({
            id: z.string().min(1),
            name: z.string().min(1),
            kind: z.string().min(1),
            url: z.string().optional(),
            content: z.string().optional(),
            version: z.number().int().positive().optional(),
            sha256: z.string().optional(),
            status: z.string().optional(),
            approvedAt: z.string().optional(),
            frozenAt: z.string().optional(),
            origin: z.enum(["OFFER", "USER", "DIRECTOR", "DETERMINISTIC_RULE"]).optional(),
        })).optional(),
    }),
    objective: z.string(),
    icp: z.object({
        industries: nonEmptyList,
        countries: nonEmptyList,
        regions: nonEmptyList,
        companySize: z.object({
            minEmployees: z.number().int().nonnegative(),
            maxEmployees: z.number().int().positive(),
            headcountRanges: z.array(z.enum(hunterHeadcounts)).min(1).optional(),
        }),
        targetRoles: nonEmptyList,
        targetRoleSource: z.enum(targetRoleSources).default("MANUAL"),
        industryMatchMode: z.enum(industryMatchModes).default("PREFERENCE"),
    }),
    targetCompanyCount: z.number().int().positive(),
    targetContactCount: z.number().int().positive(),
    meetingGoal: z.number().int().nonnegative(),
    tone: z.string(),
    restrictions: nonEmptyList,
    excludedCustomers: nonEmptyList,
    approvedClaims: nonEmptyList,
    forbiddenClaims: nonEmptyList,
    enabledChannels: z.array(z.enum(channelTypes)).min(1),
    personalization: z.object({
        publicSignalPolicy: z.enum(publicSignalPolicies).default("DISABLED"),
        allowedPublicSignalTypes: z.array(z.enum(publicSignalTypes)).default([...publicSignalTypes]),
        decisionOwner: z.literal("DIRECTOR_AI").default("DIRECTOR_AI"),
    }).default({ publicSignalPolicy: "DISABLED", allowedPublicSignalTypes: [...publicSignalTypes], decisionOwner: "DIRECTOR_AI" }),
    operationalLimits: z.object({
        maxEmailsPerDay: z.number().int().nonnegative(),
        maxWhatsAppPerDay: z.number().int().nonnegative(),
        maxCompaniesPerRun: z.number().int().positive(),
        maxContactsPerRun: z.number().int().positive(),
        requireApproval: z.boolean(),
    }),
    configVersion: z.string().min(1),
});
export const hermesCampaignResultV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    commercial_recommendation: z.enum(["ADVANCE", "ADVANCE_WITH_CHANGES", "PILOT", "NOT_RECOMMENDED"]),
    commercial_recommendation_reason: z.string().trim().min(1),
    // Compatibilidad con resultados previos. Es asesoramiento y nunca la autoridad
    // para detener automáticamente una CampaignRun.
    execution_decision: z.enum(["GO", "BLOCK"]),
    execution_decision_reason: z.string().trim().min(1),
    strategy_summary: z.string().trim().min(1),
    target_company_criteria: z.array(z.string().trim().min(1)).min(1),
    target_contact_roles: z.array(z.string().trim().min(1)).min(1),
    target_contact_role_rationale: z.array(z.object({
        role: z.string().trim().min(1),
        reason: z.string().trim().min(1),
    })).default([]),
    positioning: z.string().trim().min(1),
    value_proposition: z.string().trim().min(1),
    approved_claims: z.array(z.string().trim().min(1)),
    forbidden_claims: z.array(z.string().trim().min(1)),
    prospecting_instructions: z.string().trim().min(1),
    qualification_rules: z.array(z.string().trim().min(1)).min(1),
    outreach_strategy: z.string().trim().min(1),
    email_guidelines: z.array(z.string().trim().min(1)).min(1),
    follow_up_guidelines: z.array(z.string().trim().min(1)),
    meeting_goal: z.number().int().nonnegative(),
    risks: z.array(z.string().trim().min(1)),
    assumptions: z.array(z.string().trim().min(1)),
    missing_information: z.array(z.string().trim().min(1)),
    recommended_next_steps: z.array(z.string().trim().min(1)),
    draft_subject_pattern: z.string().trim().min(1).optional(),
    draft_opening: z.string().trim().min(1).optional(),
});
const hermesOutreachArtifactBaseV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    agent: z.string().trim().min(1).default("sdr-outreach"),
    prompt_version: z.string().trim().min(1).default("outreach-v2"),
    claims_used: z.array(z.object({
        claim: z.string().trim().min(1),
        supporting_fact: z.string().trim().min(1),
    })),
});
const outreachSourceEvidenceSchema = z.object({
    source_id: z.string().trim().min(1).max(200),
    fact: z.string().trim().min(1).max(2000),
});
const outreachSourceEvidenceArraySchema = z.preprocess((value) => Array.isArray(value)
    ? value.map((item, index) => typeof item === "string" ? { source_id: `legacy:${index + 1}`, fact: item } : item)
    : value, z.array(outreachSourceEvidenceSchema).max(20));
export const outreachMessageStrategyV2Schema = z.object({
    policy_version: z.literal("outreach-professional-v2"),
    context_mode: z.enum(["REFERRAL", "RESEARCHED", "CONTEXTUAL", "MINIMAL"]),
    objective: z.enum(["MEETING", "VALIDATE_AREA", "REQUEST_REFERRAL"]),
    focus_product_fact_id: z.string().trim().min(1).nullable(),
    prospect_signal_id: z.string().trim().min(1).nullable(),
    value_angle: z.enum(["OPERATIONAL_CONTINUITY", "SIMPLIFICATION", "VISIBILITY", "SECURITY", "CONTENT_FLOW", "ENERGY_INFRASTRUCTURE", "NEUTRAL_DISCOVERY"]),
    cta_mode: z.enum(["SHORT_MEETING", "AREA_CONFIRMATION", "CONDITIONAL_REFERRAL"]),
});
export const sendableHermesOutreachDraftV1Schema = hermesOutreachArtifactBaseV1Schema.extend({
    artifact_type: z.literal("OUTREACH_DRAFT"),
    sendable: z.literal(true),
    decision_code: z.literal("READY_FOR_HUMAN_APPROVAL"),
    subject: z.string().trim().min(1).max(180),
    body: z.string().trim().min(20).max(8000),
    bilateral_hypothesis: z.string().trim().min(12).max(1000),
    source_evidence_used: outreachSourceEvidenceArraySchema,
    prospect_evidence_used: outreachSourceEvidenceArraySchema.optional(),
    quality_warnings: z.array(z.string().max(500)).max(12).optional(),
    message_strategy: outreachMessageStrategyV2Schema.optional(),
});
export const nonSendableHermesOutreachArtifactV1Schema = hermesOutreachArtifactBaseV1Schema.extend({
    artifact_type: z.enum(["NO_EMIT", "BLOCKED", "NEEDS_INPUT"]),
    sendable: z.literal(false),
    decision_code: z.enum(["INSUFFICIENT_EVIDENCE", "RECIPIENT_INVALID", "CONTACT_NOT_VALIDATED", "ROLE_NOT_ELIGIBLE", "CLAIMS_NOT_GROUNDED", "NEEDS_INPUT"]),
    reason: z.string().trim().min(1).max(4000),
    subject: z.preprocess((value) => value === null ? undefined : value, z.string().trim().max(180).optional().default("")),
    body: z.preprocess((value) => value === null ? undefined : value, z.string().trim().max(8000).optional().default("")),
    bilateral_hypothesis: z.preprocess((value) => value === null ? undefined : value, z.string().trim().max(1000).optional().default("")),
    source_evidence_used: outreachSourceEvidenceArraySchema.optional().default([]),
    message_strategy: z.preprocess((value) => value === null ? undefined : value, outreachMessageStrategyV2Schema.optional()),
});
export const hermesOutreachDraftV1Schema = z.discriminatedUnion("artifact_type", [
    sendableHermesOutreachDraftV1Schema,
    nonSendableHermesOutreachArtifactV1Schema,
]);
export const commercialReplyDraftV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    subject: z.string().trim().min(1).max(180),
    body: z.string().trim().min(1).max(8000),
    agent: z.literal("sdr-outreach"),
    prompt_version: z.string().trim().min(1).max(80),
    claims_used: z.array(z.object({
        claim: z.string().trim().min(1),
        supporting_fact: z.string().trim().min(1),
    })).max(30),
    questions_covered: z.array(z.object({
        question_id: z.string().trim().min(1),
        answer_evidence: z.string().trim().min(3),
    })).max(20),
    grounding: z.preprocess((value) => {
        if (!value || Array.isArray(value) || typeof value !== "object")
            return value;
        return Object.entries(value).map(([source_id, detail]) => ({
            source_id,
            used_for: Array.isArray(detail) ? detail.map(String).join("; ") : String(detail),
        }));
    }, z.array(z.object({
        source_id: z.string().trim().min(1),
        used_for: z.string().trim().min(1),
    })).max(50)),
    warnings: z.array(z.string().trim().min(1).max(500)).max(20),
    generation_mode: z.literal("AI"),
});
export const commercialReplyReviewV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    approved: z.boolean(),
    reasons: z.array(z.string().trim().min(1)).max(20),
    questions_covered: z.array(z.string().trim().min(1)).max(20),
    grounding_valid: z.boolean(),
    continuity_valid: z.boolean(),
    assumptions_detected: z.array(z.string().trim().min(1)).max(20),
    warnings: z.array(z.string().trim().min(1).max(500)).max(20),
    reviewer: z.literal("director-general-ia"),
    prompt_version: z.string().trim().min(1).max(80),
});
export const contactCandidateReviewV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    approved: z.boolean(),
    reasons: z.array(z.string().trim().min(1)).max(20),
    contradictions: z.array(z.string().trim().min(1)).max(20),
    evidence_sufficient: z.boolean(),
    reviewer: z.literal("director-general-ia"),
    prompt_version: z.string().trim().min(1).max(80),
});
export const semanticClaimAuditSchema = z.object({
    schema_version: z.literal("2.0"),
    audit_ref: z.string().min(1),
    segments: z.array(z.object({
        segment_id: z.string().min(1),
        verdict: z.enum(["SUPPORTED", "UNSUPPORTED", "NON_FACTUAL"]),
        scope: z.enum(["OFFER", "PROSPECT", "MIXED", "NONE"]),
        fact_ids: z.array(z.string().min(1)),
        reason: z.string().trim().min(1),
    }).strict()).min(1),
}).strict();
export const claimAuditResultV1Schema = z.object({
    schema_version: z.literal("1.0").default("1.0"),
    factual_claims: z.array(z.object({
        claim: z.string().trim().min(1),
        supporting_fact: z.string().trim().min(1).optional(),
        supported: z.boolean(),
    })),
    semantic_audit: semanticClaimAuditSchema.optional(),
});
export const meetingInvitationDraftV1Schema = z.object({
    semanticValidation: z.object({ taskId: z.string().min(1), audit: semanticClaimAuditSchema }).strict().optional(),
    title: z.string().trim().min(5).max(180),
    objective: z.string().trim().min(10).max(600),
    agenda: z.array(z.string().trim().min(3).max(240)).min(3).max(5),
    expectedOutcome: z.string().trim().min(10).max(600),
    optionalPreparation: z.string().trim().min(3).max(500).nullable(),
    description: z.string().trim().min(20).max(4000),
    groundingEvidence: z.array(z.object({
        field: z.string().trim().min(1).max(80),
        sourceType: z.string().trim().min(1).max(80),
        sourceId: z.string().trim().min(1).max(160),
    }).strict()).max(40),
    warnings: z.array(z.string().trim().min(1).max(500)).max(20),
    generationMode: z.enum(["AI", "FALLBACK", "TEMPLATE", "MANUAL"]),
    provider: z.string().trim().min(1).max(100).nullable(),
    profile: z.string().trim().min(1).max(160).nullable(),
    model: z.string().trim().min(1).max(160).nullable(),
    promptVersion: z.string().trim().min(1).max(80),
    contentVersion: z.literal("1.0"),
}).strict().superRefine((value, context) => {
    if (value.generationMode === "AI") {
        for (const field of ["provider", "profile", "model"]) {
            if (!value[field])
                context.addIssue({ code: "custom", path: [field], message: `${field} es obligatorio para una generación AI auditable` });
        }
    }
    if (value.generationMode === "FALLBACK" && (value.provider || value.profile || value.model))
        context.addIssue({ code: "custom", path: ["generationMode"], message: "Un fallback local no puede atribuirse a un proveedor o modelo" });
});
export const updateCampaignSchema = createCampaignBaseSchema.partial().superRefine(validateWritableCampaignIndustries);
export const closerAssignmentSchema = z.object({ assigneeId: z.string().min(1).nullable() });
export const closerStatusUpdateSchema = z.object({ status: z.enum(closerOpportunityStatuses) });
export const closerNoteSchema = z.object({ body: z.string().trim().min(1).max(4000) });
export const batchApprovalSchema = z.object({
    messageIds: z.array(z.string().min(1)).min(1).max(100),
    note: z.string().trim().max(500).optional(),
});
export const pdfAssetUploadSchema = z.object({
    filename: z.string().trim().min(5).max(255),
    mimeType: z.literal("application/pdf"),
    description: z.string().trim().max(2000).default(""),
    contentBase64: z.string().min(8),
    claims: z.array(z.string().trim().min(3).max(500)).max(50).default([]),
});
export const meetingDecisionSchema = z.object({
    decision: z.enum(["APPROVE", "REJECT"]),
    note: z.string().trim().max(500).optional(),
    attendees: z.array(z.string().email()).max(25).optional(),
});
export const meetingTeamSelectionSchema = z.object({
    memberIds: z.array(z.string().min(1)).min(1, "Seleccioná al menos un integrante").max(20)
        .refine((values) => new Set(values).size === values.length, "Los integrantes no pueden repetirse"),
});
export const meetingOptionsProposalUpdateSchema = meetingTeamSelectionSchema.extend({
    options: z.array(z.object({
        startsAt: z.string().datetime(),
        endsAt: z.string().datetime(),
        timezone: z.string().trim().min(1).max(120),
    })).min(2, "Cargá al menos dos horarios").max(8),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i),
}).superRefine((value, context) => {
    const starts = new Set();
    value.options.forEach((option, index) => {
        if (Date.parse(option.endsAt) <= Date.parse(option.startsAt))
            context.addIssue({ code: "custom", path: ["options", index, "endsAt"], message: "La finalización debe ser posterior al inicio" });
        if (starts.has(option.startsAt))
            context.addIssue({ code: "custom", path: ["options", index, "startsAt"], message: "Los horarios no pueden repetirse" });
        starts.add(option.startsAt);
    });
});
export const terminalRunStatuses = ["COMPLETED", "FAILED", "BLOCKED", "CANCELLED"];
export const loginSchema = z.object({ username: z.string().trim().min(1), password: z.string().min(8) });
export const approvalDecisionSchema = z.object({ decision: z.enum(["APPROVE", "REJECT"]), note: z.string().max(500).optional() });
export const manualEmailSchema = z.object({
    campaignId: z.string().min(1),
    clientRequestId: z.string().uuid().optional(),
    sourceMessageId: z.string().min(1).optional(),
    subject: z.string().trim().min(1, "El asunto no puede quedar vacío").max(500),
    body: z.string().trim().min(20, "El correo debe tener al menos 20 caracteres").max(20_000),
    bodyHtml: z.string().trim().min(1).max(60_000).optional(),
    expectsReply: z.boolean().default(true),
});
export const approvalMessageEditSchema = z.object({
    subject: z.string().trim().min(1, "El asunto no puede quedar vacío").max(500),
    body: z.string().trim().min(20, "El correo debe tener al menos 20 caracteres").max(20_000).optional(),
    bodyHtml: z.string().trim().min(1).max(60_000).optional(),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i, "La versión esperada no es válida"),
}).superRefine((value, context) => {
    if (!value.body && !value.bodyHtml)
        context.addIssue({
            code: "custom",
            path: ["body"],
            message: "El correo no puede quedar vacío",
        });
});
export const campaignEmailTemplateTypes = [
    "INITIAL_DECISION_MAKER",
    "INITIAL_INFLUENCER",
    "INITIAL_ROUTER",
    "MEETING_OPTIONS",
    "FOLLOW_UP_1", "FOLLOW_UP_2", "REFERRAL_CLARIFICATION", "QUESTION_RESPONSE", "MEETING_OPTIONS_FOLLOW_UP", "CALENDAR_INVITATION", "CALENDAR_UPDATE", "CALENDAR_CANCELLATION",
];
export const campaignEmailTemplateVariableKeys = [
    "contact.first_name",
    "contact.last_name",
    "contact.role",
    "company.name",
    "offer.name",
    "offer.summary",
    "sender.name",
    "sender.role",
    "sender.company",
    "meeting.date_time", "meeting.starts_at",
    "meeting.ends_at",
    "meeting.duration",
    "meeting.options",
    "meeting.team_names",
    "meeting.team_with_roles",
    "meeting.team_emails",
];
export const campaignEmailTemplateUpdateSchema = z.object({
    subjectTemplate: z.string().trim().min(1).max(500),
    bodyTextTemplate: z.string().trim().min(20).max(20_000),
    bodyHtmlTemplate: z.string().trim().min(1).max(60_000).nullable().optional(),
    expectedContentHash: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
});
export const campaignEmailTemplateApplySchema = z.object({
    templateVersionId: z.string().min(1),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i),
});
export const campaignEmailAiDraftRestoreSchema = z.object({
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i),
});
export const campaignEmailTemplateBulkApplySchema = z.object({
    approvalIds: z.array(z.string().min(1)).min(1).max(100),
    mode: z.enum(["RECOMMENDED", "EXPLICIT"]).default("RECOMMENDED"),
    templateVersionId: z.string().min(1).optional(),
}).superRefine((value, context) => {
    if (value.mode === "EXPLICIT" && !value.templateVersionId)
        context.addIssue({
            code: "custom",
            path: ["templateVersionId"],
            message: "Elegí una versión de plantilla para la aplicación masiva",
        });
});
export const approvalMessageAttachmentUploadSchema = z.object({
    filename: z.string().trim().min(5).max(255),
    mimeType: z.literal("application/pdf"),
    contentBase64: z.string().min(8),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i, "La versión esperada no es válida"),
});
export const approvalMessageAttachmentRemoveSchema = z.object({
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i, "La versión esperada no es válida"),
});
export const approvalAttachmentUpdateSchema = z.object({
    include: z.boolean(),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i, "La versión esperada no es válida"),
});
export const commercialReplyRejectionSchema = z.object({
    mode: z.enum(["MANUAL_REVIEW", "REGENERATE"]),
    reason: z.string().trim().min(12, "Explicá el motivo con al menos 12 caracteres").max(1000),
    expectedPayloadHash: z.string().regex(/^[a-f0-9]{64}$/i, "El hash esperado no es válido"),
}).superRefine((value, context) => {
    const usefulWords = value.reason.match(/[\p{L}\p{N}]{2,}/gu) ?? [];
    if (usefulWords.length < 3)
        context.addIssue({
            code: "custom",
            path: ["reason"],
            message: "Describí concretamente qué debe corregirse",
        });
});
export const directionDecisionSchema = z.object({
    decision: z.enum(["ADVANCE", "ADVANCE_WITH_CONDITIONS", "REWORK", "STOP"]),
    reason: z.string().trim().max(1000).optional(),
    conditions: z.array(z.string().trim().min(1).max(500)).max(25).optional(),
}).superRefine((value, context) => {
    if (value.decision === "ADVANCE_WITH_CONDITIONS" && !value.conditions?.length) {
        context.addIssue({
            code: "custom",
            path: ["conditions"],
            message: "Avanzar con condiciones requiere al menos una condición",
        });
    }
});
export const integrationUpdateSchema = z.object({
    mode: z.enum(integrationModes),
    parameters: z.record(z.string(), z.unknown()).default({}),
    secrets: z.record(z.string(), z.string()).optional(),
    clearSecrets: z.boolean().optional()
});
